import { makeObservable, observable, action, computed } from 'mobx';
import { apiSwagger } from '@/shared/api';
import { notesStatusEvents } from '@/sharedTypes/coreTypes';
import { ManagerEntity } from '@/shared/globalFunctions';
import { debounce } from 'lodash';

interface GameState {
  currentScore: number;
  multiplier: number;
  consecutivePerfectMeasures: number;
  errorsCount: number;
  // for e2e tests
  _correctNotes: number;
  highScore: number;
  lastMeasureIndex: number;
  lastSaved?: {
    currentScore: number
    measureIndex: number
    multiplier: number
    updatedAt: string
  }
}

class GamificationStore {
  gameState: GameState = {
    currentScore: 0,
    multiplier: 1.0,
    consecutivePerfectMeasures: 0,
    errorsCount: 0,
    _correctNotes: 0,
    highScore: 0,
    lastMeasureIndex: 0,
  };

  gamificationEnabled = false;
  saveProgressEnabled = true;
  isGamificationLoading = false;
  currentSampleId: string | null = null;
  currentUserId: string | null = null;
  private listenersRegistered = false;

  private defaultStates = {
    noteSuccess: 10,
    noteFail: 10,
    measureCompleteBase: 20,
    perfectRunBonus: 100,
  };

  constructor() {
    makeObservable(this, {
      gameState: observable,
      gamificationEnabled: observable,
      saveProgressEnabled: observable,
      isGamificationLoading: observable,
      currentSampleId: observable,
      currentUserId: observable,
      isPerfectRun: computed,
      setGamificationEnabled: action,
      setSaveProgressEnabled: action,
      setGameState: action,
      setLoading: action,
      updateContext: action,
      handleNoteSuccess: action,
      handleNoteFail: action,
      handleMeasureSuccess: action,
      handlePracticeEnd: action,
    });

    // Initialize gamification state from localStorage
    if (typeof window !== 'undefined') {
      this.gamificationEnabled = localStorage.getItem('gamificationEnabled') === 'true';
      this.saveProgressEnabled = localStorage.getItem('saveProgressEnabled') !== 'false';
      this.setupEventListeners();
      this.setupStorageListener();
    }
  }

  get isPerfectRun() {
    return this.gameState.errorsCount === 0;
  }

  // !! For e2e tests
  setGamificationStorageState = (gameState: GameState) => {
    if(typeof window !== 'undefined') {
      localStorage.setItem("gamificationState", JSON.stringify(gameState));
    }
  }

  setGamificationEnabled = (enabled: boolean) => {
    this.gamificationEnabled = enabled;
    if (typeof window !== 'undefined') {
      localStorage.setItem('gamificationEnabled', enabled ? 'true' : 'false');
      
      // Dispatch storage event for cross-tab sync
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'gamificationEnabled',
        newValue: enabled ? 'true' : null,
        oldValue: !enabled ? 'true' : null
      }));
    }
  };

  setSaveProgressEnabled = (enabled: boolean) => {
    this.saveProgressEnabled = enabled;
    if(typeof window !== 'undefined') {
      localStorage.setItem('saveProgressEnabled', enabled ? 'true' : 'false');

      // Dispatch storage event for cross-tab sync
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'saveProgressEnabled',
        newValue: enabled ? 'true' : null,
        oldValue: !enabled ? 'true' : null
      }));
    }
  }

  setGameState = (newState: Partial<GameState>) => {
    this.gameState = { ...this.gameState, ...newState };
    this.setGamificationStorageState({ ...this.gameState, ...newState })
  };

  setLoading = (loading: boolean) => {
    this.isGamificationLoading = loading;
  };

  updateContext = (sampleId: string | null, userId: string | null) => {
    // const sampleIdChanged = this.currentSampleId !== sampleId;
    
    this.currentSampleId = sampleId;
    this.currentUserId = userId;
    
    // Load progress when we have both sampleId and userId, and only when sample changes
    if (sampleId && userId && this.gamificationEnabled) {
      this.loadProgress();
    }
  };

  private setupStorageListener = () => {
    const handleStorageChange = () => {
      const storedValue = localStorage.getItem('gamificationEnabled') === 'true';
      this.gamificationEnabled = storedValue;
    };

    window.addEventListener('storage', handleStorageChange);
  };

  private setupEventListeners = () => {
    if (this.listenersRegistered) {
      return;
    }

    window.addEventListener(notesStatusEvents.NOTE_SUCCESS, this.handleNoteSuccess);
    window.addEventListener(notesStatusEvents.NOTE_FAILED, this.handleNoteFail);
    window.addEventListener(notesStatusEvents.ALL_NOTES_IN_MEASURE_FINISHED, this.handleMeasureSuccess);
    window.addEventListener(notesStatusEvents.PRACTICE_END, this.handlePracticeEnd);

    this.listenersRegistered = true;
  };

  handleNoteSuccess = (e: Event) => {
    if (!this.gamificationEnabled) return;
    
    const { measureIndex } = (e as CustomEvent).detail || {};
    
    const newScore = this.gameState.currentScore + this.defaultStates.noteSuccess;
    const updatedState = {
      currentScore: newScore,
      highScore: Math.max(this.gameState.highScore, newScore),
      lastMeasureIndex: measureIndex !== undefined ? 
        Math.max(this.gameState.lastMeasureIndex, measureIndex) : 
        this.gameState.lastMeasureIndex,
      _correctNotes: this.gameState._correctNotes + 1
    };
    
    this.setGameState(updatedState);
    this.saveProgress();
      
    // Trigger PIXI bubble effect
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('showNoteBubble', {
        detail: { scoreDelta: this.defaultStates.noteSuccess }
      }));
    }
  };

  handleNoteFail = (e: Event) => {
    if (!this.gamificationEnabled) return;
    
    const { measureIndex } = (e as CustomEvent).detail || {};
    
    const updatedState = {
      currentScore: Math.max(0, this.gameState.currentScore - this.defaultStates.noteFail),
      errorsCount: this.gameState.errorsCount + 1,
      multiplier: 1.0, // reset
      consecutivePerfectMeasures: 0, // reset
      lastMeasureIndex: measureIndex !== undefined ? 
        Math.max(this.gameState.lastMeasureIndex, measureIndex) : 
        this.gameState.lastMeasureIndex,
    };
    
    this.setGameState(updatedState);
    this.saveProgress();
    
    // Trigger PIXI bubble effect for failed notes
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('showNoteBubble', {
        detail: { scoreDelta: -this.defaultStates.noteFail, isFailed: true }
      }));
    }
  };

  handleMeasureSuccess = (e: Event) => {
    if (!this.gamificationEnabled) return;
    const { measureIndex, areErrorsInMeasure } = (e as CustomEvent).detail;
    // const areErrorsInSample = this.gameState.errorsCount > 0;
    const newConsecutive = !areErrorsInMeasure ? this.gameState.consecutivePerfectMeasures + 1 : 0;
    const newMultiplier = !areErrorsInMeasure ? 
      (newConsecutive % 5 === 0 ? this.gameState.multiplier + 0.1 : this.gameState.multiplier) : 
      1.0;

    let newScore = this.gameState.currentScore;
    if (!areErrorsInMeasure) {
      const measureBonus = Math.round(this.defaultStates.measureCompleteBase * this.gameState.multiplier);
      newScore = this.gameState.currentScore + measureBonus;
    }
    
    const updatedState = {
      currentScore: newScore,
      highScore: Math.max(this.gameState.highScore, newScore),
      consecutivePerfectMeasures: newConsecutive,
      multiplier: newMultiplier,
      lastMeasureIndex: measureIndex,
    };
    
    this.setGameState(updatedState);
    this.saveProgress();
  };

  handlePracticeEnd = () => {
    if (!this.gamificationEnabled) return;
    
    let finalScore = this.gameState.currentScore;
    let bonusAdded = 0;
    
    // bonus for perfect run
    if (this.gameState.errorsCount === 0) {
      finalScore += this.defaultStates.perfectRunBonus;
      bonusAdded = this.defaultStates.perfectRunBonus;
    }
    
    const updatedState = {
      currentScore: finalScore,
      highScore: Math.max(this.gameState.highScore, finalScore),
    };
    
    this.setGameState(updatedState);
    this.saveProgressImmediately(); // Force immediate save for practice end
    
    // Trigger PIXI practice end effect
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('showPracticeEnd', {
        detail: { scoreDelta: bonusAdded }
      }));
    }
  };

  loadProgress = async () => {
    if (!this.currentSampleId || !this.currentUserId) return;
    
    this.setLoading(true);
    try {
      const response = await apiSwagger.api.gameStatsDetail(this.currentUserId, this.currentSampleId);
      const { progress } = response.data;
      let newState: Partial<GameState> = {};
      if (progress) {
        // Got progress but saveProgress is disabled -> reset progress
        if(!this.saveProgressEnabled) {
          newState = {
            currentScore: 0,
            multiplier: 1.0,
            consecutivePerfectMeasures: 0,
            errorsCount: 0,
            highScore: progress.stats?.highestScore || 0,
            lastMeasureIndex: 0,
          };
        } else {
          // Got progress and saveProgress is enabled -> load progress from last save
          newState = {
            currentScore: progress.stats?.lastSaved?.currentScore || 0,
            multiplier: progress.stats?.lastSaved?.multiplier || 1.0,
            // need to handle the consecutivePerfectMeasures, errorsCount as well on back
            consecutivePerfectMeasures: 0,
            errorsCount: 0,
            highScore: progress.stats?.highestScore || 0,
            lastMeasureIndex: progress.stats?.lastSaved?.measureIndex || 0,
          }
        }
        this.setGameState(newState);

        // Move player to the saved measure position if saveProgress is enabled and there's actual progress
        if (this.saveProgressEnabled && newState.lastMeasureIndex !== undefined && newState.lastMeasureIndex > 0) {
          // Move to the next measure after the last completed one
          const targetMeasure = newState.lastMeasureIndex + 1;
          // Add a longer delay to ensure everything is loaded
          setTimeout(() => {
            this.movePlayerToMeasure(targetMeasure);
          }, 800);
        }
      }
    } catch (error) {
      console.error('Failed to load progress for sample', this.currentSampleId, ':', error);
    } finally {
      this.setLoading(false);
    }
  };

  private saveProgressDebounced: (() => void) | null = null;

  private saveProgress = () => {
    if (!this.currentSampleId || !this.currentUserId || !this.saveProgressEnabled) return;

    // Create debounced save function if it doesn't exist
    if (!this.saveProgressDebounced) {
      // debounce is imported above, need to test if it works like that
      // const { debounce } = require('lodash');
      this.saveProgressDebounced = debounce(async () => {
        await this.saveProgressImmediately();
      }, 2000, { leading: false, trailing: true });
    }

    this.saveProgressDebounced!();
  };

  saveProgressImmediately = async () => {
    if (!this.currentSampleId || !this.currentUserId) return;

    this.setLoading(true);
    try {
      await apiSwagger.api.gameStatsCreate(this.currentUserId, this.currentSampleId, {
        measureIndex: this.gameState.lastMeasureIndex,
        currentScore: this.gameState.currentScore,
        multiplier: this.gameState.multiplier,
      });
    } catch (error) {
      console.error('Failed to save progress:', error);
    } finally {
      this.setLoading(false);
    }
  };

  resetProgress = async () => {
    if (!this.currentSampleId || !this.currentUserId) return;
    
    try {
      await apiSwagger.api.gameStatsDelete(this.currentUserId, this.currentSampleId);
      
      this.setGameState({
        currentScore: 0,
        multiplier: 1.0,
        consecutivePerfectMeasures: 0,
        errorsCount: 0,
        highScore: this.gameState.highScore, // preserve high score
        lastMeasureIndex: 0,
      });
      
      this.setLoading(false);
      
      // Reset player state
      this.resetPlayerState();
    } catch (error) {
      console.error('Failed to reset progress:', error);
    }
  };

  private movePlayerToMeasure = (measureIndex: number) => {
    try {
      if (ManagerEntity && ManagerEntity.player && ManagerEntity.core && ManagerEntity.renderer) {
        // Use a longer delay to ensure everything is fully loaded
        setTimeout(() => {
          try {
            // Get the timemap from the core
            const timemap = ManagerEntity.core._timemap || ManagerEntity.core.timemap;
            if (timemap && timemap[measureIndex]) {
              const measure = timemap[measureIndex];

              // Find the first note in the specified measure
              if (measure.notes && measure.notes.length > 0) {
                const firstNoteInMeasure = measure.notes[0];

                // Collect all notes from completed measures to mark as successfully played
                // If we're moving to measure N, then measures 0 through N-1 should be marked as completed
                const notesToMarkAsSuccess: any[] = [];
                for (let i = 0; i < measureIndex; i++) {
                  if (timemap[i] && timemap[i].notes) {
                    notesToMarkAsSuccess.push(...timemap[i].notes);
                  }
                }

                // Mark all previous notes as successfully played (green)
                if (notesToMarkAsSuccess.length > 0 && typeof ManagerEntity.renderer.setNotesToSuccessStatus === 'function') {
                  ManagerEntity.renderer.setNotesToSuccessStatus(notesToMarkAsSuccess);
                }

                // Use moveToNote to go to the first note of the measure
                if (typeof ManagerEntity.player.moveToNote === 'function') {
                  ManagerEntity.player.moveToNote(firstNoteInMeasure, false, true);
                } else {
                  console.error('[Gamification] moveToNote method not found on player');
                }
              } else {
                console.error(`[Gamification] No notes found in measure ${measureIndex}`);
              }
            } else {
              console.error(`[Gamification] Measure ${measureIndex} not found in timemap`);
            }
          } catch (innerErr) {
            console.error('[Gamification] Failed to move player to measure:', innerErr);
          }
        }, 500);
      } else {
        console.error('[Gamification] ManagerEntity, player, core, or renderer not available');
      }
    } catch (err) {
      console.error('[Gamification] Failed to move player to measure:', err);
    }
  };

  private resetPlayerState = () => {
    try {
      if (ManagerEntity && ManagerEntity.player) {
        // Move to first note with reset
        if (typeof ManagerEntity.player.goToFirstNote === 'function') {
          ManagerEntity.player.goToFirstNote(true);
        }
        
        // Reset practice mode state
        if (ManagerEntity.selectedPracticeMode && typeof ManagerEntity.selectedPracticeMode.reset === 'function') {
          ManagerEntity.selectedPracticeMode.reset();
        }
        
        // Re-enable practice mode after reset
        if (ManagerEntity.selectedPracticeMode) {
          ManagerEntity.selectedPracticeMode.disable = false;
          
          // Ensure it stays enabled
          setTimeout(() => {
            if (ManagerEntity.selectedPracticeMode?.disable === true) {
              ManagerEntity.selectedPracticeMode.disable = false;
            }
          }, 100);
        }
        
        // Clear failed notes state
        if (ManagerEntity.selectedPracticeMode && typeof ManagerEntity.selectedPracticeMode.clearAllFailedNotes === 'function') {
          ManagerEntity.selectedPracticeMode.clearAllFailedNotes();
        }
        
        // Reset notes colors
        if (ManagerEntity.renderer && typeof ManagerEntity.renderer.resetNotesColor === 'function') {
          ManagerEntity.renderer.resetNotesColor();
        }
        
        // Refresh nearest notes
        if (ManagerEntity.selectedPracticeMode && typeof ManagerEntity.selectedPracticeMode.refreshNearestNotes === 'function') {
          ManagerEntity.selectedPracticeMode.refreshNearestNotes();
        }
      }
    } catch (err) {
      console.error('Failed to reset player state:', err);
    }
  };

  toggleGamification = (enabled?: boolean) => {
    const newValue = enabled ?? !this.gamificationEnabled;
    this.setGamificationEnabled(newValue);
  };

  toggleSaveProgress = (enabled?: boolean) => {
    const newValue = enabled ?? !this.saveProgressEnabled;
    this.setSaveProgressEnabled(newValue);
  }
}

export default GamificationStore;